import { useState, useRef, useEffect, useCallback } from 'react';

type BindingPrimitive = string | number | bigint | boolean | null;
type BindingArray = readonly (BindingPrimitive | BindingArray)[];

export type BindingValue = BindingPrimitive | BindingArray | undefined;

/**
 * Two-way binding helper.
 *
 * Example:
 * ```ts
 * const Example: React.FC<{ selected?: string }> = (props) => {
 *   const [selected, onSelectedChange] = useBinding(props.selected, (ev: React.ChangeEvent<HTMLSelectElement>) => ev.target.value);
 *
 *   return (
 *     <select value={selected} onChange={onSelectedChange}>
 *       ...options
 *     </select>
 *   );
 * };
 * ```
 */
export function useBinding<T extends BindingValue, A extends any[]>(
  initialValue: T,
  reducer: (...args: A) => T,
  ...handlers: ((value: T) => void)[]
): [T, (...args: A) => void] {
  // Convert to JSON so that array values can't be mutated, and for deep
  // equality checking.
  const jsonValue = toJson(initialValue);
  const [value, setValue] = useState(() => fromJson<T>(jsonValue));
  const initializedRef = useRef(false);
  const reducerRef = useRef(reducer);
  const handlersRef = useRef(handlers);

  // Update reducer and handlers refs so that the onChange function remains
  // stable, even if the reducer or handlers change.
  reducerRef.current = reducer;
  handlersRef.current = handlers;

  // Update the state value if the initial value changes (deep equality check).
  // This should NOT call the reducer or handlers, because the change didn't
  // originate from the returned onChange function.
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
    } else {
      setValue(fromJson<T>(jsonValue));
    }
  }, [jsonValue]);

  const onChange = useCallback((...args: A): void => {
    const newValue = reducerRef.current(...args);
    // Copy so that the callbacks can't mutate an array value in place.
    setValue(copy(newValue));
    handlersRef.current.forEach(callback => callback(newValue));
  }, []);

  return [value, onChange];
}

function toJson(value: BindingValue): string | undefined {
  return value === undefined ? value : JSON.stringify(value);
}

function fromJson<T extends BindingValue>(value: string | undefined): T {
  return value === undefined ? value : JSON.parse(value);
}

function copy<T extends BindingValue>(value: T): T {
  return fromJson<T>(toJson(value));
}
